/**
 * The level's rail while levels are agent-first (lib/agent-first.ts): the
 * learner's own agent, what it has spent on this run, and the saving on this
 * level once it has passed twice.
 *
 * Until the tab is lent to an agent it is the whole of the setup: sign in,
 * then one button that makes a key and lends the tab, and one command to
 * paste (lib/agent-command.ts) that starts Claude Code with the server and
 * the cost report (worker/ai-telemetry.ts) set for that run. The command
 * stays on the rail until the agent's first call. The longer way, settings
 * that last, is folded underneath. One key does both the server and the report.
 *
 * The figures are the meter's (worker/ai-meter.ts), read every few seconds
 * while the agent is connected and the tab is visible. The saving is
 * `practiceComparison`: the first agent pass is the baseline, every later run
 * of the level is charged to "after", failures included. Replay resets the
 * level, so a replay always starts from the seed, never from solved work.
 */
import { useEffect, useState } from 'react';
import type { PracticeComparison } from '@engineer-os/engine';
import type { PublicMission } from '../../types';
import { connectBridge, disconnectBridge } from '../../lib/agent-bridge';
import { fetchFeedback, fetchMeter } from '../../lib/efficiency';
import { reportSettings } from '../../lib/agent-keys';
import { agentCommand, likelyShell, type Shell } from '../../lib/agent-command';
import { intlTag } from '../../lib/format';
import { agentFirstCopy } from '../../lib/strings/agent-first';
import { useLocale, useStrings } from '../../state/useLocale';
import { Button } from '../../ui';
import { agentCall, Copy } from '../agent/AgentKeys';
import { useBridge } from './AgentStrip';
import { CostPractice } from './CostPractice';

/** How often the rail reads the meter while an agent is working. */
const POLL_MS = 8000;

/**
 * The key the one button made, for this tab only: the command needs it again
 * after a replay or a moment offline, and a key a minute would soon hit the
 * five-key limit. Gone when the tab closes.
 */
const KEY_STORE = 'engineeros.agentKey';
function storedKey(): string | null {
  try {
    return sessionStorage.getItem(KEY_STORE);
  } catch {
    return null;
  }
}
function storeKey(key: string): void {
  try {
    sessionStorage.setItem(KEY_STORE, key);
  } catch {
    // Without storage the command still shows; a reload asks for a new key.
  }
}

interface Spend {
  costMicro: number;
  calls: number;
  /** The run has ended: these are the last run's figures, not a live count. */
  done: boolean;
}

export function AgentRail({ mission, userId, checkpoint, passed, onSignIn, onReplay }: {
  mission: PublicMission;
  userId: string | null;
  checkpoint: boolean;
  /** This workspace passed its last check: read the meter again for the saving. */
  passed: boolean;
  onSignIn(): void;
  onReplay(): void;
}) {
  const { locale } = useLocale();
  const s = agentFirstCopy[locale];
  const strings = useStrings().agents;
  const { state, log } = useBridge();
  const on = state === 'on' || state === 'connecting' || state === 'lost';

  if (!userId) {
    return (
      <section className="block agent-rail">
        <h2 className="label">{s.gateTitle}</h2>
        <p>{s.gateIntro}</p>
        <Button variant="solid" wide onClick={onSignIn}>{s.signIn}</Button>
      </section>
    );
  }

  if (!on) return <Setup taken={state === 'taken-over'} />;
  const key = storedKey();

  return (
    <>
      <section className="block agent-rail" aria-live="polite">
        <p className="agent-strip-line">
          <strong>{state === 'on' ? strings.connected : state === 'lost' ? strings.lost : strings.connecting}</strong>
          <button type="button" className="btn btn-quiet" onClick={disconnectBridge}>{strings.stop}</button>
        </p>
        {/* The command shows while the tab is still connecting: it is what the learner does next. */}
        {log.length === 0 && (key ? <Command agentKey={key} /> : state === 'on' && <p className="note">{s.waiting}</p>)}
        {/* The learner holds the task and talks to their agent (owner, 2026-10-30): the agent sees the files, not the brief. */}
        <p className="note agent-rail-talk"><strong>{s.talkTitle}.</strong> {s.talkHint}</p>
        {log.length > 0 && (
          <ul className="agent-log">
            {log.map((entry) => (
              <li key={`${entry.at}:${entry.what}:${entry.detail}`} className={entry.failed ? 'is-fail' : undefined}>
                {strings.did[entry.what]} {entry.detail && <code dir="ltr">{entry.detail}</code>}
              </li>
            ))}
          </ul>
        )}
      </section>
      <Meter missionId={mission.id} checkpoint={checkpoint} passed={passed} onReplay={onReplay} />
    </>
  );
}

/** Everything between a signed-in learner and a lent tab: one button. */
function Setup({ taken }: { taken: boolean }) {
  const { locale } = useLocale();
  const s = agentFirstCopy[locale];
  const strings = useStrings().agents;
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const start = async () => {
    setProblem(null);
    if (!storedKey()) {
      setBusy(true);
      const { status, data } = await agentCall('/api/agent/keys', { name: s.keyName });
      setBusy(false);
      if (status !== 201 || typeof data?.secret !== 'string') {
        setProblem(status === 409 ? s.keyTooMany : s.keyFailed);
        return;
      }
      storeKey(data.secret);
    }
    connectBridge();
  };

  return (
    <section className="block agent-rail">
      <h2 className="label">{s.gateTitle}</h2>
      <p>{s.gateIntro}</p>
      {taken && <p className="note">{strings.takenOver}</p>}
      <p><Button variant="solid" wide onClick={() => void start()} disabled={busy}>{s.getCommand}</Button></p>
      {problem && (
        <p className="note" role="alert">
          {problem} {/* A new tab: a page load here would throw the running machine away. */}
          <a href="/agent" target="_blank" rel="noopener">{s.keyPage}</a>
        </p>
      )}
      <Lasting />
    </section>
  );
}

/** The command to paste, in the shell this visitor most likely has. */
function Command({ agentKey }: { agentKey: string }) {
  const { locale } = useLocale();
  const s = agentFirstCopy[locale];
  const [shell, setShell] = useState<Shell>(() => likelyShell(navigator.platform));
  const command = agentCommand(window.location.origin, agentKey, shell);
  return (
    <div className="agent-rail-command">
      <p><strong>{s.pasteTitle}</strong></p>
      <p className="agent-rail-shells" role="group" aria-label={s.shellLabel}>
        {(['posix', 'powershell'] as const).map((each) => (
          <button key={each} type="button" className="btn btn-quiet" aria-pressed={shell === each} onClick={() => setShell(each)}>
            {each === 'posix' ? s.shellPosix : s.shellPowershell}
          </button>
        ))}
      </p>
      <div className="aikey__codewrap">
        <pre dir="ltr" className="aikey__code"><code>{command}</code></pre>
        <Copy text={command} />
      </div>
      <p className="note">{s.pasteHint}</p>
      <p className="note">{s.keyKept}</p>
    </div>
  );
}

/** The command for the next run, folded: the rail's log has replaced it by now. */
function Again() {
  const { locale } = useLocale();
  const key = storedKey();
  if (!key) return null;
  return (
    <details className="agent-rail-lasting">
      <summary>{agentFirstCopy[locale].again}</summary>
      <Command agentKey={key} />
    </details>
  );
}

/** The longer way: a key, a server and a report that stay set in Claude Code. */
function Lasting() {
  const { locale } = useLocale();
  const s = agentFirstCopy[locale];
  const server = new URL('/api/mcp', window.location.origin).href;
  const shown = storedKey() ?? '<your key>';
  const addLine = `claude mcp add --transport http engineeros ${server} --header "Authorization: Bearer ${shown}"`;
  const report = reportSettings(window.location.origin, shown);
  return (
    <details className="agent-rail-lasting">
      <summary>{s.lastingTitle}</summary>
      <ol className="agent-rail-steps">
        <li>
          <strong>{s.step2}</strong>
          <pre dir="ltr" className="aikey__code"><code>{addLine}</code></pre> <Copy text={addLine} />
        </li>
        <li>
          <strong>{s.step3}</strong>
          <p className="note">{s.step3Hint}</p>
          <pre dir="ltr" className="aikey__code"><code>{report}</code></pre> <Copy text={report} />
        </li>
      </ol>
      <p className="note">{s.otherAgents}</p>
    </details>
  );
}

/** What this run has spent, and the level's saving once there is one. Also the desk's (components/learner-desk/). */
export function Meter({ missionId, checkpoint, passed, onReplay }: { missionId: string; checkpoint: boolean; passed: boolean; onReplay(): void }) {
  const { locale } = useLocale();
  const s = agentFirstCopy[locale];
  // Four decimals: a level's run costs fractions of a cent, and "under a cent" would hide the difference.
  const cost = (micro: number) => new Intl.NumberFormat(intlTag(locale), { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(micro / 1e6);
  const [spend, setSpend] = useState<Spend | null>(null);
  const [practice, setPractice] = useState<PracticeComparison | null>(null);

  useEffect(() => {
    let live = true;
    let timer = 0;
    let passId: string | null = null;
    const read = async () => {
      if (document.visibilityState === 'visible') {
        const meter = await fetchMeter();
        if (!live) return;
        // The run in progress; between runs, the last one, so a finished run
        // never reads as "no calls" (a visitor, 2026-10-28). Newest first.
        const run = meter?.attempts.find((a) => a.ref === missionId && a.outcome === 'open') ?? meter?.attempts.find((a) => a.ref === missionId);
        setSpend(run ? { costMicro: run.costMicro, calls: run.calls, done: run.outcome !== 'open' } : null);
        // The latest agent pass of this level carries the comparison; read
        // its feedback only when that pass changes.
        const pass = meter?.shown ? meter.attempts.find((a) => a.ref === missionId && a.outcome === 'passed' && a.by === 'agent') : undefined;
        if (!checkpoint && pass && pass.id !== passId) {
          passId = pass.id;
          const feedback = await fetchFeedback(pass.id);
          if (live) setPractice(feedback?.practice ?? null);
        }
      }
      if (live) timer = window.setTimeout(() => void read(), POLL_MS);
    };
    void read();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [missionId, checkpoint, passed]);

  return (
    <>
      <section className="block agent-rail">
        <h2 className="label">{spend?.done && spend.calls > 0 ? s.lastRun : s.spent}</h2>
        {spend && spend.calls > 0 ? (
          <p className="agent-rail-spend">
            <strong>{cost(spend.costMicro)}</strong> <span className="note">{s.calls(spend.calls)}</span>
          </p>
        ) : (
          <p className="note">{s.noCalls}</p>
        )}
      </section>
      {practice && (
        <section className="block agent-rail">
          <h2 className="label">{s.saving}</h2>
          {practice.after ? (
            <CostPractice comparison={practice} missionId={missionId} />
          ) : (
            <>
              <p className="agent-rail-spend">{s.firstPass} <strong>{cost(practice.before.costMicro)}</strong></p>
              <p className="note">{s.replayIntro}</p>
              <Again />
            </>
          )}
          <Button variant={practice.after ? 'quiet' : 'solid'} wide onClick={onReplay}>{s.replay}</Button>
        </section>
      )}
    </>
  );
}
